import React, { useState } from 'react'
import {
  Box,
  Paper,
  Stack,
  Typography,
  Collapse,
  Skeleton,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material'
import {
  syncDotColor,
  syncLabel,
  type SyncFreshnessEntry,
} from '../../../utils/syncFreshness'

export type { SyncFreshnessEntry }

export interface SnapshotMetric {
  label: string
  value: string
  alertColor?: string
}

interface SystemSnapshotCardProps {
  icon: React.ReactNode
  systemName: string
  syncEntry?: SyncFreshnessEntry | null
  metrics: SnapshotMetric[]
  metricsLoading?: boolean
  breakdown?: React.ReactNode
}

export const SystemSnapshotCard: React.FC<SystemSnapshotCardProps> = ({
  icon,
  systemName,
  syncEntry,
  metrics,
  metricsLoading = false,
  breakdown,
}) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        height: '100%',
        borderRadius: 2,
        borderColor: 'grey.200',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        {icon}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {systemName}
        </Typography>
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: syncDotColor(syncEntry),
            flexShrink: 0,
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {syncLabel(syncEntry)}
        </Typography>
      </Stack>

      {metricsLoading ? (
        <Skeleton variant="rounded" height={60} sx={{ mt: 2 }} />
      ) : (
        <Stack
          direction="row"
          sx={{
            mt: 2,
            flexWrap: 'wrap',
            rowGap: 2,
          }}
        >
          {metrics.map((m) => (
            <Box
              key={m.label}
              sx={{
                flex: '1 1 0',
                minWidth: 72,
                textAlign: 'center',
                px: 0.5,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: m.alertColor || 'text.primary',
                }}
              >
                {m.value}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.25, display: 'block' }}
              >
                {m.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      {breakdown && (
        <Box sx={{ mt: 'auto', pt: 2 }}>
          <Box
            onClick={() => setExpanded(!expanded)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' },
              transition: 'color 0.15s',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {expanded ? 'Hide breakdown' : 'View breakdown'}
            </Typography>
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: 16, ml: 0.25 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16, ml: 0.25 }} />
            )}
          </Box>
          <Collapse in={expanded}>
            <Box sx={{ mt: 1 }}>{breakdown}</Box>
          </Collapse>
        </Box>
      )}
    </Paper>
  )
}

export default SystemSnapshotCard
